import React, { useState, useEffect } from "react";
import { supabase } from "../supabase";

function Settings({ userId }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [slidesUrl, setSlidesUrl] = useState("");
  const [newGroupName, setNewGroupName] = useState("");
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [contentType, setContentType] = useState("url"); // "url" or "file"

  useEffect(() => {
    fetchGroups();
    fetchCurrentConfig();
  }, [selectedGroup]);

  const fetchGroups = async () => {
    const { data, error } = await supabase
      .from("tv_groups")
      .select("*")
      .order("name");

    if (error) {
      console.error("Error fetching groups:", error);
      return;
    }

    setGroups(data);
  };

  const fetchCurrentConfig = async () => {
    if (!selectedGroup) return;

    const { data, error } = await supabase
      .from("slides_config")
      .select("*")
      .eq("group_id", selectedGroup)
      .single();

    if (error && error.code !== "PGRST116") {
      // PGRST116 is "no rows returned"
      console.error("Error fetching config:", error);
      return;
    }

    if (data) {
      setSlidesUrl(data.slides_url);
    } else {
      setSlidesUrl("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      setSuccess(false);

      // Check if config exists
      const { data: existing } = await supabase
        .from("slides_config")
        .select("*")
        .eq("group_id", selectedGroup)
        .single();

      if (existing) {
        // Update existing config
        const { error: updateError } = await supabase
          .from("slides_config")
          .update({
            slides_path: slidesUrl,
            updated_at: new Date().toISOString(),
          })
          .eq("id", existing.id);

        if (updateError) throw updateError;
      } else {
        // Only handle URL submissions here - file uploads are handled in the file input onChange
        if (contentType === "url") {
          // Insert new config
          const { error: insertError } = await supabase
            .from("slides_config")
            .insert({
              group_id: selectedGroup,
              slides_path: slidesUrl,
              created_by: userId,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            });

          if (insertError) throw insertError;
        }
      }

      setSuccess(true);
    } catch (error) {
      console.error("Error saving config:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-semibold mb-4">Slides Configuration</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-red-50 p-4 rounded text-red-600 text-sm">
              {error}
            </div>
          )}
          {success && (
            <div className="bg-green-50 p-4 rounded text-green-600 text-sm">
              Configuration saved successfully!
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Select Group
            </label>
            <div>
              <select
                value={selectedGroup}
                onChange={(e) => setSelectedGroup(e.target.value)}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                required
              >
                <option value="">Select a group...</option>
                {groups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mb-4">
            <div className="flex border-b border-gray-200">
              <button
                type="button"
                className={`py-2 px-4 ${
                  contentType === "url"
                    ? "border-b-2 border-indigo-500 text-indigo-600"
                    : "text-gray-500 hover:text-gray-700"
                }`}
                onClick={() => setContentType("url")}
              >
                URL
              </button>
              <button
                type="button"
                className={`py-2 px-4 ${
                  contentType === "file"
                    ? "border-b-2 border-indigo-500 text-indigo-600"
                    : "text-gray-500 hover:text-gray-700"
                }`}
                onClick={() => setContentType("file")}
              >
                File Upload
              </button>
            </div>

            {contentType === "url" && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700">
                  Video URL
                </label>
                <input
                  type="url"
                  value={slidesUrl}
                  onChange={(e) => setSlidesUrl(e.target.value)}
                  placeholder="https://example.com/video.mp4"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>
            )}

            {contentType === "file" && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700">
                  Upload Video
                </label>
                <input
                  type="file"
                  accept="video/*"
                  onChange={async (e) => {
                    if (e.target.files?.[0]) {
                      try {
                        setLoading(true);
                        const file = e.target.files[0];
                        const fileExt = file.name.split(".").pop();
                        const fileName = `${Math.random()}.${fileExt}`;
                        const filePath = `${selectedGroup}/${fileName}`;

                        const { error: uploadError } = await supabase.storage
                          .from("slides")
                          .upload(filePath, file);

                        if (uploadError) {
                          setError(uploadError.message);
                          return;
                        }

                        const { error: configError } = await supabase
                          .from("slides_config")
                          .upsert({
                            group_id: selectedGroup,
                            slides_path: filePath,
                            created_by: userId,
                            created_at: new Date().toISOString(),
                            updated_at: new Date().toISOString(),
                          });

                        if (configError) {
                          setError(configError.message);
                          return;
                        }

                        setSuccess(true);
                      } catch (err) {
                        setError(err.message);
                      } finally {
                        setLoading(false);
                      }
                    }
                  }}
                  className="mt-1 block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-md file:border-0
                  file:text-sm file:font-semibold
                  file:bg-indigo-50 file:text-indigo-700
                  hover:file:bg-indigo-100"
                />
              </div>
            )}
          </div>

          <div>
            <button
              type="submit"
              disabled={loading || !selectedGroup}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-400"
            >
              {loading ? "Saving..." : "Save Configuration"}
            </button>
          </div>
        </form>
      </div>
      <div className="bg-white shadow rounded-lg p-6 mt-6">
        <div>
          <label className="block text-sm font-medium text-gray-700">
            New Group Name
          </label>
          <div className="mt-1 flex rounded-md shadow-sm">
            <input
              type="text"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              className="flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-l-md border border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              placeholder="Enter group name"
            />
            <button
              type="button"
              onClick={async () => {
                try {
                  const { data, error } = await supabase
                    .from("tv_groups")
                    .insert({
                      name: newGroupName,
                      created_by: userId,
                    })
                    .select()
                    .single();

                  if (error) throw error;

                  setSelectedGroup(data.id);
                  setNewGroupName("");
                  setShowNewGroup(false);
                  fetchGroups();
                } catch (error) {
                  setError(error.message);
                }
              }}
              className="inline-flex items-center px-3 py-2 border border-l-0 border-gray-300 rounded-r-md bg-gray-50 hover:bg-gray-100 text-gray-500 sm:text-sm"
            >
              Add
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Settings;
